/*
 * Контекстное меню у выбранной грани или ребра — как в 123D Design.
 *
 * ЗАЧЕМ. В 123D клик по грани поднимает рядом маленькую шестерёнку, а под ней —
 * набор действий, применимых именно к этому элементу (стр. 7 и 25 методички
 * МИНа). Это сильно быстрее, чем каждый раз искать инструмент в ленте, и
 * особенно важно детям: видно, ЧТО можно сделать с тем, на что ты нажал.
 *
 * Набор действий зависит от выбранного:
 *   грань  → эскиз на ней, выдавить, отверстие, текст, оболочка;
 *   ребро  → скругление, фаска.
 *
 * Меню живёт в 3D-сцене через <Html> из drei, поэтому в Viewport оно должно
 * стоять на ПОСТОЯННОЙ позиции среди детей (см. комментарий там): Html
 * вставляет DOM в обход React, и появление соседей перед ним роняло редактор.
 */
import React, { useState } from 'react';
import { Html } from '@react-three/drei';
import useCadStore from '../store/useCadStore';
import { buildFaceProfile } from '../sketch/faceProfileBuilder';
import {
    IconSketch, IconExtrude, IconHole, IconShell,
    IconFillet, IconChamfer, IconSketchText,
} from '../ui/icons';


const btnStyle = {
    display: 'flex', alignItems: 'center', gap: 7,
    width: '100%', padding: '7px 11px',
    background: 'none', border: 'none', borderRadius: 6,
    color: '#e8eaf0', font: '13px/1.2 system-ui, sans-serif',
    cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
};


export default function FaceActionMenu() {
    const selection = useCadStore((s) => s.selection);
    const faceData = useCadStore((s) => s.selectedFaceData);
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const mode = useCadStore((s) => s.mode);
    const [open, setOpen] = useState(false);

    // Пока идёт операция или мы в эскизе — меню молчит: там свой сценарий.
    const isFace = selection.kind === 'face' && !!faceData?.planeRef?.center;
    const isEdge = selection.kind === 'edge' && selection.ids.length > 0;
    const visible = mode === 'view3d' && !pendingFeature && (isFace || isEdge);

    // Меню сворачивается, как только выбор сменился.
    React.useEffect(() => { setOpen(false); }, [selection.kind, selection.ids.join(',')]);

    if (!visible) return null;

    // Точка привязки: центр грани. Для ребра берём середину его подсветки,
    // если она есть, иначе меню не показываем (нечего привязывать).
    const anchor = isFace ? faceData.planeRef.center : faceData?.planeRef?.center;
    if (!anchor) return null;

    const store = () => useCadStore.getState();

    const actions = isFace ? [
        {
            id: 'sketch', label: 'Эскиз на грани', Icon: IconSketch,
            run: () => window.__kubikonCadSketchOnFace?.(faceData.planeRef),
        },
        {
            id: 'extrude', label: 'Выдавить грань', Icon: IconExtrude,
            run: () => {
                const profile = buildFaceProfile(faceData);
                store().setPendingFeature({
                    kind: 'extrude', faceProfile: profile,
                    targetBodyId: faceData.bodyId,
                    distance: 10, direction: 'one', operation: 'add',
                });
            },
        },
        {
            id: 'hole', label: 'Отверстие', Icon: IconHole,
            run: () => store().setPendingFeature({
                kind: 'hole', planeRef: faceData.planeRef,
                faceCenter: faceData.planeRef.center, targetBodyId: faceData.bodyId,
                offsetX: 0, offsetY: 0, diameter: 5, depth: 10, depthType: 'through',
            }),
        },
        {
            id: 'text', label: 'Текст на грани', Icon: IconSketchText,
            run: () => {
                const st = store();
                window.__kubikonCadSketchOnFace?.(faceData.planeRef);
                if (!st.textDraft) st.setTextDraft({ text: '', fontSize: 10 });
                st.setActiveSketchTool('sketchText');
            },
        },
        {
            id: 'shell', label: 'Оболочка', Icon: IconShell,
            run: () => store().setPendingFeature({
                kind: 'shell', targetBodyId: faceData.bodyId,
                faces: [{ bodyId: faceData.bodyId, faceId: faceData.faceId,
                          center: faceData.planeRef.center }],
                thickness: 2,
            }),
        },
    ] : [
        {
            id: 'fillet', label: 'Скругление', Icon: IconFillet,
            run: () => store().setPendingFeature({ kind: 'fillet', edgeRefs: [], radius: 3 }),
        },
        {
            id: 'chamfer', label: 'Фаска', Icon: IconChamfer,
            run: () => store().setPendingFeature({ kind: 'chamfer', edgeRefs: [], radius: 2 }),
        },
    ];

    return (
        <Html position={anchor} center zIndexRange={[90, 0]}>
            <div style={{ pointerEvents: 'auto', userSelect: 'none' }}
                 onPointerDown={(e) => e.stopPropagation()}>
                {/* Свёрнутое состояние — та самая «шестерёнка» из 123D. */}
                {!open && (
                    <button
                        onClick={() => setOpen(true)}
                        title="Что можно сделать"
                        style={{
                            width: 30, height: 30, borderRadius: '50%',
                            background: 'rgba(28,32,40,0.95)',
                            border: '1px solid #4a5568', color: '#e8b73a',
                            cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,.4)',
                        }}
                    >
                        <GearIcon/>
                    </button>
                )}

                {open && (
                    <div style={{
                        background: 'rgba(28,32,40,0.97)',
                        border: '1px solid #3a4252', borderRadius: 8,
                        padding: 4, minWidth: 168,
                        boxShadow: '0 6px 20px rgba(0,0,0,.5)',
                    }}>
                        {actions.map(({ id, label, Icon, run }) => (
                            <button
                                key={id}
                                style={btnStyle}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#2a3140'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                onClick={() => { setOpen(false); run(); }}
                            >
                                <Icon size={15}/> {label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </Html>
    );
}


/** Шестерёнка — SVG, а не эмодзи (правило проекта). */
const GearIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
);
